import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import secureLocalStorage from "react-secure-storage";
import { getData_Any2Column } from "../service/APIService";
import config from "../config.json";

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const [isSubscribed, setIsSubscribed] = useState(() => {
    if (config.pro === true) return true;
    if (config.pro === false) return false;
    return secureLocalStorage.getItem("is_subscribed") === true;
  });
  const [recipeCount, setRecipeCount] = useState(() => {
    const loggedInUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
    if (!loggedInUser) {
      return parseInt(localStorage.getItem("guest_scan_count") || "0", 10);
    }
    return 0;
  });
  const [loading, setLoading] = useState(false);
  const [isNativeApp, setIsNativeApp] = useState(() => {
    return secureLocalStorage.getItem("is_native_app") === true;
  });

  const loggedInUser = JSON.parse(secureLocalStorage.getItem("loggedInUser"));
  const userEmail = loggedInUser?.email || "guest";

  // Bridge detection logic from PassKit app
  const checkNativeMarkers = useCallback(() => {
    if (typeof window === "undefined") return false;
    const hasWKWebView = !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.subscribe);
    const hasRNWebView = !!(window.ReactNativeWebView);
    const hasAndroidBridge = !!(window.Android && typeof window.Android.subscribe === "function");
    const searchParams = new URLSearchParams(window.location.search);
    const hasUrlParam = searchParams.get("isApp") === "true";
    return hasWKWebView || hasRNWebView || hasAndroidBridge || hasUrlParam;
  }, []);

  useEffect(() => {
    if (checkNativeMarkers()) {
      setIsNativeApp(true);
      secureLocalStorage.setItem("is_native_app", true);
    }

    // Set up native bridge handlers
    window.onSubscriptionUpdate = (isActive) => {
      setIsSubscribed(isActive);
      secureLocalStorage.setItem("is_subscribed", isActive);
    };

    return () => {
      delete window.onSubscriptionUpdate;
    };
  }, [checkNativeMarkers]);

  useEffect(() => {
    secureLocalStorage.setItem("is_subscribed", isSubscribed);
  }, [isSubscribed]);

  const refreshRecipeCount = useCallback(async () => {
    if (userEmail === "guest") {
      const gCount = parseInt(localStorage.getItem("guest_scan_count") || "0", 10);
      setRecipeCount(gCount);
      return;
    }
    setLoading(true);
    try {
      const data = await getData_Any2Column("email", userEmail, "type", "recipe-ai");
      // Logged-in users are limited per-day.
      // Track consumed quota so deleting a recipe does NOT refund/restore daily quota.
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const todayCount = data.filter(item => item.date && item.date.startsWith(today)).length;

      const recipeData = JSON.parse(secureLocalStorage.getItem("recipeData")) || {};
      const recordedUsage = recipeData[today]?.[userEmail] || 0;
      const effectiveCount = Math.max(todayCount, recordedUsage);

      if (effectiveCount > recordedUsage) {
        if (!recipeData[today]) recipeData[today] = {};
        recipeData[today][userEmail] = effectiveCount;
        secureLocalStorage.setItem("recipeData", JSON.stringify(recipeData));
      }

      setRecipeCount(effectiveCount);
    } catch (err) {
      console.error("Error fetching recipe count:", err);
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const incrementScanCount = useCallback(() => {
    if (userEmail === "guest") {
      const newCount = recipeCount + 1;
      setRecipeCount(newCount);
      localStorage.setItem("guest_scan_count", newCount.toString());
    } else {
      refreshRecipeCount();
    }
  }, [userEmail, recipeCount, refreshRecipeCount]);

  useEffect(() => {
    refreshRecipeCount();
  }, [refreshRecipeCount, isSubscribed]);

  const handleSubscribe = () => {
    if (window.ReactNativeWebView) {
      // Works for both iOS (via injectedJavaScript bridge) and Android WebView
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'SUBSCRIBE', data: 'start' }));
    } else if (window.webkit?.messageHandlers?.subscribe) {
      // iOS WKWebView direct handler (fallback if ReactNativeWebView not available)
      window.webkit.messageHandlers.subscribe.postMessage('start');
    } else if (window.Android?.subscribe) {
      // Android native bridge fallback
      window.Android.subscribe('start');
    } else {
      console.log('Subscription triggered (Fallback - web/testing)');
    }
  };

  const handleRestore = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'RESTORE', data: 'start' }));
    } else if (window.webkit?.messageHandlers?.restore) {
      window.webkit.messageHandlers.restore.postMessage('start');
    } else if (window.Android?.restore) {
      window.Android.restore();
    } else {
      console.log('Restore triggered (Fallback - web/testing)');
    }
  };

  const handleOpenExternal = (url) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'OPEN_EXTERNAL',
        url: url
      }));
    } else if (window.webkit?.messageHandlers?.openExternal) {
      window.webkit.messageHandlers.openExternal.postMessage(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const reachedLimit = !isSubscribed && (
    userEmail === "guest"
      ? recipeCount >= config.guestCount
      : recipeCount >= config["non-pro-dailyLimit"]
  );

  return (
    <SubscriptionContext.Provider value={{
      isSubscribed,
      setIsSubscribed,
      recipeCount,
      refreshRecipeCount,
      incrementScanCount,
      reachedLimit,
      loading,
      freelimitcount: config.freelimitcount,
      dailyLimit: config["non-pro-dailyLimit"],
      isNativeApp,
      handleSubscribe,
      handleRestore,
      handleOpenExternal,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);
